import React, { createContext, useContext, useMemo, useCallback } from "react";
import {
  useMsal,
  useIsAuthenticated,
  useAccount,
} from "@azure/msal-react";
import { InteractionStatus, AccountInfo, IdTokenClaims, AuthenticationResult } from "@azure/msal-browser";
import { checkAdminStatus } from "./SharePointService";
import { loginRequest, apiTokenRequest } from "./MsalConfig";
import { fetchAndLogEntraUserInfo, fetchUserMemberGroupIds, validateTenantAccess } from "./EntraService";
import { useCompany } from "./CompanyContext";
import { TenantMismatchData } from "../Components/TenantAccessDeniedModal";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface User {
  id: string;
  name: string;
  email: string;
  role: "User" | "Supervisor" | "Admin" | "Basic User";
  photoUrl?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: (accountToLogout?: AccountInfo | null) => Promise<void>;
  getAccessToken: () => Promise<string>;
  tenantMismatch: TenantMismatchData | null;
  clearTenantMismatch: () => void;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/**
 * Extract the application role from the ID-token claims.
 */
const resolveRole = (account: AccountInfo): "User" | "Supervisor" => {
  const claims = account.idTokenClaims as IdTokenClaims & { roles?: string[] };
  const roles = claims?.roles ?? [];

  if (roles.includes("Supervisor")) return "Supervisor";
  return "User";
};

const mapAccountToUser = (account: AccountInfo): User => ({
  id: account.localAccountId,
  name: account.name ?? "Unknown User",
  email: account.username, // UPN / email
  role: resolveRole(account),
});

/* ------------------------------------------------------------------ */
/*  Context                                                            */
/* ------------------------------------------------------------------ */

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount(accounts[0] ?? ({} as AccountInfo));
  const { company } = useCompany();

  /* ---- state for enriched user and tenant security ---- */
  const [enrichedUser, setEnrichedUser] = React.useState<User | null>(null);
  const [isRoleLoading, setIsRoleLoading] = React.useState(false);
  const [tenantMismatch, setTenantMismatch] = React.useState<TenantMismatchData | null>(null);

  const clearTenantMismatch = useCallback(() => {
    setTenantMismatch(null);
  }, []);

  const isLoading = (inProgress !== InteractionStatus.None) || isRoleLoading;

  /* ---- derived user ---- */
  const baseUser: User | null = useMemo(() => {
    if (!isAuthenticated || !account) return null;
    return mapAccountToUser(account);
  }, [isAuthenticated, account]);

  /* ---- check tenant group membership & SharePoint role ---- */
  React.useEffect(() => {
    const checkRole = async () => {
      // 1. IMPORTANT: Allow public card preview without restriction
      // Anyone scanning a QR code (/preview/:id) must be able to view without auth or kick-outs
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/preview/')) {
        return;
      }

      if (!baseUser || !account) {
        setEnrichedUser(null);
        return;
      }

      try {
        setIsRoleLoading(true);

        // 2. Validate tenant group membership via Microsoft Graph /me/memberOf
        const memberGroupIds = await fetchUserMemberGroupIds(instance, account);
        const validation = validateTenantAccess(company.id, memberGroupIds);

        if (!validation.isValid) {
          console.warn(
            `🚫 [Security Policy Enforcement] Access denied on ${company.shortName} portal:`,
            validation.reason
          );

          // Store mismatch details for modal feedback
          setTenantMismatch({
            isOpen: true,
            reason: validation.reason || 'Access denied for this organization.',
            userCompany: validation.userCompany || 'unknown',
            attemptedCompany: company,
            account: account,
          });

          localStorage.removeItem("accessToken");
          setEnrichedUser(null);
          setIsRoleLoading(false);
          return;
        }

        // If authorized, clear any previous mismatch alert
        setTenantMismatch(null);

        // 3. Log all Microsoft Entra ID user information
        fetchAndLogEntraUserInfo(instance);

        // 4. Try to get token for SharePoint
        try {
          const response: AuthenticationResult = await instance.acquireTokenSilent({
            scopes: ["https://ekedp.sharepoint.com/AllSites.Read"],
            account: account,
          });

          const isAdmin = await checkAdminStatus(baseUser.email, response.accessToken);
          
          let photoUrl: string | undefined = undefined;
          try {
            // Acquire a separate token for Microsoft Graph
            const graphResponse = await instance.acquireTokenSilent({
              scopes: ["User.Read"],
              account: account,
            });

            const photoResponse = await fetch("https://graph.microsoft.com/v1.0/me/photo/$value", {
              headers: {
                Authorization: `Bearer ${graphResponse.accessToken}`,
              },
            });
            if (photoResponse.ok) {
              const blob = await photoResponse.blob();
              photoUrl = URL.createObjectURL(blob);
            }
          } catch (photoError) {
            console.warn("Failed to fetch user photo:", photoError);
          }

          setEnrichedUser({
            ...baseUser,
            role: isAdmin ? "Admin" : "Basic User",
            photoUrl,
          });
        } catch (spError) {
          console.error("Failed to fetch SharePoint role:", spError);
          setEnrichedUser({
            ...baseUser,
            role: "Basic User",
          });
        }
      } catch (error) {
        console.error("Failed during user authentication / validation:", error);
        setEnrichedUser(null);
      } finally {
        setIsRoleLoading(false);
      }
    };

    if (isAuthenticated && baseUser) {
      checkRole();
    } else {
      setEnrichedUser(null);
    }
  }, [isAuthenticated, baseUser, account, instance, company]);

  /* ---- login ---- */
  const login = useCallback(async () => {
    try {
      setTenantMismatch(null);
      await instance.loginPopup(loginRequest);
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  }, [instance]);

  /* ---- logout ---- */
  const logout = useCallback(async (accountToLogout?: AccountInfo | null) => {
    try {
      setTenantMismatch(null);
      const targetAccount = accountToLogout || account || accounts[0];
      await instance.logoutPopup({
        mainWindowRedirectUri: "/",
        account: targetAccount || undefined,
      });
    } catch (error) {
      console.warn("Logout popup dismissed or failed:", error);
    } finally {
      try {
        await instance.setActiveAccount(null);
      } catch (e) {
        console.error("Failed to clear active account:", e);
      }
      localStorage.removeItem("accessToken");
      setEnrichedUser(null);
    }
  }, [instance, account, accounts]);

  /* ---- acquire token silently (for API calls) ---- */
  const getAccessToken = useCallback(async (): Promise<string> => {
    if (!account) throw new Error("No authenticated account");

    try {
      const response = await instance.acquireTokenSilent({
        ...apiTokenRequest,
        account,
      });
      return response.accessToken;
    } catch (error) {
      // If silent acquisition fails, fall back to popup
      const response = await instance.acquireTokenPopup(apiTokenRequest);
      return response.accessToken;
    }
  }, [instance, account]);

  /* ---- context value ---- */
  const value = useMemo<AuthContextType>(
    () => ({
      user: enrichedUser,
      isAuthenticated,
      isLoading,
      login,
      logout,
      getAccessToken,
      tenantMismatch,
      clearTenantMismatch,
    }),
    [enrichedUser, isAuthenticated, isLoading, login, logout, getAccessToken, tenantMismatch, clearTenantMismatch]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};
